import { resolveActivityTypeConfig } from './activityTypeConfig.js';

export function getLogbookEntryTypeRule(entryType, entryTypeMeta = null) {
  const resolvedConfig = resolveActivityTypeConfig(entryTypeMeta || {}, entryType);

  return {
    row4Mode: resolvedConfig.timeEntryMode,
    autoResponsibleCurrentUser: resolvedConfig.autoResponsibleMode === 'current-user',
    autoHoursZero: resolvedConfig.hoursQuestionMode === 'hidden',
    showPauseQuestion: resolvedConfig.pauseMode === 'question',
    askPdfProtocol: resolvedConfig.pdfProtocolMode === 'ask',
    serviceDurationLabel: resolvedConfig.serviceDurationLabel,
    protocolCode: resolvedConfig.protocolCode || null,
  };
}

export function calculateRawServiceHours(timeFrom, timeTo) {
  if (!timeFrom || !timeTo) {
    return null;
  }

  const [fromH, fromM] = String(timeFrom).split(':').map(Number);
  const [toH, toM] = String(timeTo).split(':').map(Number);

  if ([fromH, fromM, toH, toM].some((value) => Number.isNaN(value))) {
    return null;
  }

  let diff = (toH + (toM / 60)) - (fromH + (fromM / 60));
  if (diff < 0) {
    diff += 24;
  }

  return Number(diff.toFixed(2));
}

export function getPauseHoursForDuration(duration, pauseAnswer) {
  if (duration === null || pauseAnswer !== 'ja') {
    return 0;
  }
  if (duration <= 6) {
    return 0;
  }
  if (duration <= 9) {
    return 0.5;
  }
  return 0.75;
}

export function getNoBreakCompensationPauseHours(duration) {
  if (duration === null || !Number.isFinite(Number(duration))) {
    return 0;
  }

  if (duration <= 6) {
    return 0;
  }
  if (duration <= 9) {
    return 0.5;
  }
  return 0.75;
}

export function getEffectivePauseHoursForWizard(entryType, duration, pauseAnswer, enforcedNoBreakPauseHours = 0) {
  if (duration === null) {
    return 0;
  }

  const rule = getLogbookEntryTypeRule(entryType);
  if (!rule.showPauseQuestion) {
    return 0;
  }

  if (pauseAnswer === 'ja') {
    return getPauseHoursForDuration(duration, 'ja');
  }

  if (pauseAnswer === 'nein') {
    const explicitNoBreakPause = Number(enforcedNoBreakPauseHours);
    if (Number.isFinite(explicitNoBreakPause) && explicitNoBreakPause > 0) {
      return explicitNoBreakPause;
    }

    return getNoBreakCompensationPauseHours(duration);
  }

  return 0;
}

export function getPersistedPauseHours(entry) {
  const explicitPauseHours = Number(entry?.pause_hours);
  if (Number.isFinite(explicitPauseHours) && explicitPauseHours > 0) {
    return explicitPauseHours;
  }

  const rule = getLogbookEntryTypeRule(entry?.entry_type);
  if (!rule.showPauseQuestion) {
    return 0;
  }

  const pauseTaken = Number(entry?.pause_taken) === 1 || entry?.pause_taken === true;
  const serviceHours = calculateRawServiceHours(entry?.time_from, entry?.time_to);
  if (!pauseTaken || serviceHours === null) {
    return 0;
  }

  return getPauseHoursForDuration(serviceHours, 'ja');
}

export function calculateEffectiveServiceHoursForEntry(entry) {
  const rawServiceHours = calculateRawServiceHours(entry?.time_from, entry?.time_to);
  if (rawServiceHours === null) {
    return null;
  }

  const pauseHours = getPersistedPauseHours(entry);
  return Math.max(0, Number((rawServiceHours - pauseHours).toFixed(2)));
}

export function calculateEffectiveServiceHoursForWizard(entryType, timeFrom, timeTo, pauseAnswer, enforcedNoBreakPauseHours = 0) {
  const rawServiceHours = calculateRawServiceHours(timeFrom, timeTo);
  if (rawServiceHours === null) {
    return null;
  }

  const pauseHours = getEffectivePauseHoursForWizard(entryType, rawServiceHours, pauseAnswer, enforcedNoBreakPauseHours);
  return Math.max(0, Number((rawServiceHours - pauseHours).toFixed(2)));
}